import { useEffect, useState } from "react";
import { Tags, Button } from "../../../components/ui";
import hydrationVestImage from "../assets/hydration_vest.svg";
import minimizeGlyph from "../assets/minimize.svg";
import { resolveRecoImageSrc } from "../utils/resolveRecoImageSrc.js";
import { amazonUrlForRec } from "../utils/amazonLinks.js";
import RecoRatingStars from "./RecoRatingStars.jsx";
import RecoPrice from "./RecoPrice.jsx";
import carouselStyles from "./ReminderRecoCarousel.module.css";

function stopSwipeDrag(e) {
  e.stopPropagation();
}

/**
 * Featured (expanded) reco card shell matching {@link ReminderRecoCarousel} — used on the
 * swipe deck so visuals stay aligned with hub carousels (Figma Reco Card instances).
 */
export default function GifteeRecoSwipeCard({
  rec,
  className,
  isDraggable,
  dragStyle,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onTransitionEnd,
  onAnimationEnd,
}) {
  const brand = rec.brand || "Gift Gaff";
  const sizes = rec.sizes;
  const colors = rec.colors;
  const productUrl = amazonUrlForRec(rec);

  const [imgFallbackLocal, setImgFallbackLocal] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    setImgFallbackLocal(false);
  }, [rec.id, rec.imageUrl, rec.imageSrc, rec.thumbnailUrl]);

  useEffect(() => {
    setIsCollapsed(false);
  }, [rec.id]);

  const imageSrc = imgFallbackLocal
    ? hydrationVestImage
    : resolveRecoImageSrc(rec, hydrationVestImage);

  const mergedClass = [
    carouselStyles.miniCard,
    isCollapsed
      ? carouselStyles.compactRecoCard
      : carouselStyles.featuredRecoSlide,
    !isCollapsed ? carouselStyles.swipeDeckCard : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article
      role={isDraggable ? "presentation" : undefined}
      className={mergedClass}
      style={dragStyle}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onTransitionEnd={onTransitionEnd}
      onAnimationEnd={onAnimationEnd}
    >
      {isCollapsed ? (
        <>
          <button
            type="button"
            className={carouselStyles.compactCornerBtn}
            aria-label={`Expand details for ${rec.title}`}
            onPointerDown={stopSwipeDrag}
            onClick={(e) => {
              stopSwipeDrag(e);
              setIsCollapsed(false);
            }}
          >
            <img src={minimizeGlyph} alt="" className={carouselStyles.compactCornerGlyph} />
          </button>
          <div className={carouselStyles.compactRecoBody}>
            <div className={carouselStyles.compactTopBlock}>
              <p className={carouselStyles.compactBrand}>{brand.toUpperCase()}</p>
              <h3 className={carouselStyles.compactTitle}>{rec.title}</h3>
              <RecoPrice priceText={rec.priceText} className={carouselStyles.compactPrice} />
            </div>

            <div className={carouselStyles.compactSpecsStack}>
              {sizes?.length ? (
                <div className={carouselStyles.compactSpecGroup}>
                  <p className={carouselStyles.compactSpecLabel}>Sizes</p>
                  <div className={carouselStyles.compactChipRow}>
                    {sizes.map((size) => (
                      <span key={`${rec.id}-s-${size}`} className={carouselStyles.compactChip}>
                        {size}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {colors?.length ? (
                <div className={carouselStyles.compactSpecGroup}>
                  <p className={carouselStyles.compactSpecLabel}>Colors</p>
                  <div className={carouselStyles.compactChipRow}>
                    {colors.map((color) => (
                      <span key={`${rec.id}-c-${color}`} className={carouselStyles.compactChip}>
                        {color}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {(rec.tags || []).length ? (
                <div className={carouselStyles.compactSpecGroup}>
                  <p className={carouselStyles.compactSpecLabel}>Interests</p>
                  <div className={carouselStyles.compactChipRow}>
                    {rec.tags.map((tag) => (
                      <span key={`${rec.id}-t-${tag}`} className={carouselStyles.compactChip}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className={carouselStyles.compactSpecGroup}>
                <p className={carouselStyles.compactSpecLabel}>Brand</p>
                <div className={carouselStyles.compactChipRow}>
                  <span className={carouselStyles.compactChip}>{brand}</span>
                </div>
              </div>

              <RecoRatingStars rating={rec.rating} reviewCount={rec.reviewCount} />
            </div>
          </div>
          <Button
            className={carouselStyles.recoCardCta}
            variant="primary"
            fullWidth
            href={productUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            aria-label={`Buy ${rec.fullTitle || rec.title} on Amazon`}
            onPointerDown={stopSwipeDrag}
            onClick={stopSwipeDrag}
          >
            Buy on Amazon
          </Button>
        </>
      ) : (
        <>
          <div className={carouselStyles.featureImageBlock}>
            <button
              type="button"
              className={carouselStyles.imageCornerBtn}
              aria-label={`Show compact details for ${rec.title}`}
              onPointerDown={stopSwipeDrag}
              onClick={(e) => {
                stopSwipeDrag(e);
                setIsCollapsed(true);
              }}
            >
              <img src={minimizeGlyph} alt="" className={carouselStyles.imageCornerGlyph} />
            </button>
            <img
              src={imageSrc}
              alt=""
              aria-hidden="true"
              className={carouselStyles.productImage}
              onError={() => setImgFallbackLocal(true)}
            />
          </div>
          <div className={carouselStyles.tagRow}>
            {(rec.tags || []).map((tag) => (
              <Tags
                key={`${rec.id}-${tag}`}
                label={tag}
                color="brandPrimary"
                variant="filled"
              />
            ))}
          </div>
          <div className={carouselStyles.featureBody}>
            <div className={carouselStyles.frontRecoCopy}>
              <div className={carouselStyles.frontRecoHeading}>
                <p className={carouselStyles.frontRecoBrand}>{brand.toUpperCase()}</p>
                <h3 className={carouselStyles.frontRecoTitle}>{rec.title}</h3>
              </div>
              <RecoPrice priceText={rec.priceText} className={carouselStyles.frontRecoPrice} />
            </div>
          </div>
          <Button
            className={`${carouselStyles.recoCardCta} ${carouselStyles.frontRecoCardCta}`}
            variant="primary"
            fullWidth
            href={productUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            aria-label={`Buy ${rec.fullTitle || rec.title} on Amazon`}
            onPointerDown={stopSwipeDrag}
            onClick={stopSwipeDrag}
          >
            Buy on Amazon
          </Button>
        </>
      )}
    </article>
  );
}
